import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Plus, Clock, X, Calendar as CalendarIcon, Lock, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { getEmployeeColor, getEmployeeColorLight } from '@/lib/colorUtils';
import { useAuth } from '@/lib/AuthContext';
import { useEmailPreview, EmailPreviewFrame } from '@/components/EmailPreview';
import { RdvEmailConfirmationDialog } from '@/components/crm/RdvEmailConfirmationDialog';
import { formatLocalTime, formatLocalDate } from '@/utils/formatLocalTime'; // 🆕 [P1 SUITE+]
import { GooglePlacesAutocomplete } from '@/components/shared/GooglePlacesAutocomplete';  // 🐛 [Phase 99 Feature #5]

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Rôles qui voient TOUS les calendriers sans restriction
// Rôles qui peuvent voir les calendriers des AUTRES employés admin
const ROLES_VOIR_AUTRES = ['proprietaire', 'adjoint_admin'];
// Rôles admin dont le calendrier est visible dans le sélecteur
const ROLES_ADMIN_CALENDRIER = ['proprietaire', 'representant', 'representante', 'admin', 'adjoint_admin', 'charge_projet'];

// 🆕 [3B] Helper pour détecter si un "employee" (qui peut aussi être un user
// owner/representant injecté côté backend) doit apparaître dans les dropdowns
// "Sélectionner un représentant". Tolère plusieurs formes de schéma DB.
const ROLES_REPRESENTANT_VALIDES = new Set([
  'proprietaire', 'charge_projet', 'adjoint_admin', 'adjoint',
  'representant', 'representante', 'rep',
]);
function isRepresentantCandidate(emp) {
  if (!emp) return false;
  if (emp.is_representant === true) return true;
  const candidates = [emp.role, emp.type_employe, emp.role_admin, emp.role_terrain]
    .filter(Boolean)
    .map(v => String(v).toLowerCase());
  return candidates.some(v => ROLES_REPRESENTANT_VALIDES.has(v));
}

const JOURS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const JOURS_SEMAINE = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Constantes vue semaine
const HOUR_HEIGHT = 64; // px par heure
const HOURS_START = 7;  // 7h
const HOURS_END = 21;   // 21h

const timeToMinutes = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

const addOneHour = (t) => {
  if (!t) return '10:00';
  const [h, m] = t.split(':').map(Number);
  return `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Génère les colonnes pour les événements qui se chevauchent
const getDayEventColumns = (dayEvents) => {
  if (!dayEvents.length) return { layout: {}, totalCols: 0 };
  const sorted = [...dayEvents].sort((a, b) => timeToMinutes(a.heure_debut) - timeToMinutes(b.heure_debut));
  const cols = [];
  const result = {};

  for (const ev of sorted) {
    const start = timeToMinutes(ev.heure_debut);
    const end = timeToMinutes(ev.heure_fin || addOneHour(ev.heure_debut));
    let placed = false;
    for (let c = 0; c < cols.length; c++) {
      if (start >= cols[c]) {
        cols[c] = end;
        result[ev.id] = c;
        placed = true;
        break;
      }
    }
    if (!placed) {
      result[ev.id] = cols.length;
      cols.push(end);
    }
  }
  return { layout: result, totalCols: cols.length };
};

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Dimanche
  d.setDate(d.getDate() - day); // Recule au Dimanche
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekDays = (startDate) =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return d;
  });

// Générer créneaux de 15 minutes de 8h à 18h
const genererCreneaux = () => {
  const creneaux = [];
  for (let h = 8; h < 18; h++) {
    for (let m = 0; m < 60; m += 15) {
      creneaux.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  return creneaux;
};

export function CalendrierRendezVous() {
  const [searchParams] = useSearchParams();
  const dossierId = searchParams.get('dossier_id');
  const { user } = useAuth();
  const scrollRef = useRef(null);

  // Seuls propriétaire et adjoint peuvent voir les calendriers des autres
  const canViewOthers = user && ROLES_VOIR_AUTRES.includes(user.role);
  const myEmployeeId = user?.employee_id;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCreneau, setSelectedCreneau] = useState(null);
  // Tout le monde commence sur son propre calendrier
  const [filtreEmploye, setFiltreEmploye] = useState(() => user?.employee_id || 'tous');
  const [viewMode, setViewMode] = useState('month');

  const [showIndispo, setShowIndispo] = useState(false);
  const [showCeduler, setShowCeduler] = useState(false);
  const [showAutreRdv, setShowAutreRdv] = useState(false);
  const [showModifierRdv, setShowModifierRdv] = useState(false);
  const [rdvAModifier, setRdvAModifier] = useState(null);
  const [rdvDetail, setRdvDetail] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreview, setEmailPreview] = useState({ sujet: '', contenu: '', email: '', _preview: false });
  const [pendingRdvData, setPendingRdvData] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const { previewHtml: rdvPreviewHtml, loadingPreview: rdvLoadingPreview, fetchPreview: rdvFetchPreview } = useEmailPreview();
  
  // 🆕 Modal de sélection représentant quand on arrive depuis fiche client
  const [showSelectRepresentant, setShowSelectRepresentant] = useState(false);
  const [selectedRepresentantTemp, setSelectedRepresentantTemp] = useState('');

  const [indisponibilites, setIndisponibilites] = useState([]);
  const [rendezVous, setRendezVous] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [conges, setConges] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    titre: '', date: '', heure_debut: '09:00', heure_fin: '10:00', description: '', invites: [], recurrence: 'aucune',
    // 🐛 [Phase 99] Nouveaux champs
    adresse: '', adresse_lat: null, adresse_lng: null, adresse_place_id: null,
    mode_visibilite: 'moi_et_invites', // moi_seul | moi_et_invites | invites_seulement
    rappels_config: { envoyer_24h: true, envoyer_1h: true, delais_custom_minutes: [] },
  });

  const [nouvelleIndispo, setNouvelleIndispo] = useState({
    representant_id: '', representant_nom: '', date_debut: '',
    date_fin: '', raison: 'Indisponible', toute_journee: true, recurrence: 'aucune',
  });

  const [nouveauRdv, setNouveauRdv] = useState({
    type_rdv: 'autre', titre: '', employe_id: '',
    employe_nom: '', date: '', heure_debut: '09:00', notes: '',
  });

  useEffect(() => {
    fetchData();
    if (dossierId) fetchDossier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, dossierId, viewMode, currentWeekStart, filtreEmploye]);

  // 🐛 [Phase 99 Bug #2] Auto-select today au mount pour que le panel détail
  // ne soit pas vide sur mobile (avant : "Cliquez sur une date pour voir les créneaux"
  // → l'utilisatrice pensait qu'il n'y avait aucun RDV).
  useEffect(() => {
    if (!selectedDate) {
      setSelectedDate(new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🆕 Ouvrir le modal de sélection de représentant quand on arrive depuis la fiche client
  useEffect(() => {
    if (dossierId && dossier && employees.length > 0 && !selectedDate) {
      // Si le dossier a déjà un représentant assigné, le pré-sélectionner et basculer sur son calendrier
      if (dossier.representant_id) {
        setSelectedRepresentantTemp(dossier.representant_id);
        setFiltreEmploye(dossier.representant_id);
        toast.info(`Calendrier de ${dossier.representant_nom || 'ce représentant'} affiché`);
      } else {
        // Sinon, ouvrir le modal pour choisir
        setShowSelectRepresentant(true);
      }
    }
  }, [dossierId, dossier, employees, selectedDate]);

  // Scroll vers 8h au chargement en vue semaine
  useEffect(() => {
    if (viewMode === 'week' && scrollRef.current) {
      const scrollTo = (8 - HOURS_START) * HOUR_HEIGHT - 16;
      scrollRef.current.scrollTop = Math.max(0, scrollTo);
    }
  }, [viewMode]);

  // Sync semaine quand on passe en vue semaine
  const savedMonthDate = useRef(null);
  const handleViewModeChange = (mode) => {
    if (mode === 'week') {
      savedMonthDate.current = new Date(currentDate);
      const base = selectedDate || currentDate;
      setCurrentWeekStart(getWeekStart(base));
    } else if (mode === 'month' && savedMonthDate.current) {
      // Restaurer le mois d'origine au lieu de rester sur le lundi de la semaine
      setCurrentDate(savedMonthDate.current);
      savedMonthDate.current = null;
    }
    setViewMode(mode);
  };

  const fetchData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      // En vue semaine, charger aussi le mois suivant/précédent si la semaine chevauche 2 mois
      const monthsToFetch = [[year, month]];
      if (viewMode === 'week') {
        const wd = getWeekDays(currentWeekStart);
        const endMonth = wd[6].getMonth() + 1;
        const endYear = wd[6].getFullYear();
        if (endYear !== year || endMonth !== month) {
          monthsToFetch.push([endYear, endMonth]);
        }
      }

      // 🆕 [Phase 31 #3] Si un employé est sélectionné, fetcher TOUS ses RDV
      // sur 12 mois passés + 6 mois futurs via date_debut/date_fin.
      const employeFiltre = filtreEmploye && filtreEmploye !== 'tous' ? filtreEmploye : null;
      let rdvPromises;
      if (employeFiltre) {
        const today = new Date();
        const dDebut = new Date(today.getFullYear(), today.getMonth() - 12, 1);
        const dFin = new Date(today.getFullYear(), today.getMonth() + 6, 0);
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        rdvPromises = [axios.get(`${API_URL}/api/crm/rendez-vous?date_debut=${fmt(dDebut)}&date_fin=${fmt(dFin)}&employe_id=${employeFiltre}`)];
      } else {
        rdvPromises = monthsToFetch.map(([y, m]) => axios.get(`${API_URL}/api/crm/rendez-vous?year=${y}&month=${m}`));
      }
      const indispoPromises = monthsToFetch.map(([y, m]) => axios.get(`${API_URL}/api/crm/indisponibilites?year=${y}&month=${m}`));

      const [empRes, congesRes, ...rest] = await Promise.all([
        // 🆕 [3B] include_owners + include_representants_users : récupère aussi
        // les proprietaires (ex: Nass) et les users marqués is_representant
        // pour qu'ils apparaissent dans les dropdowns "Sélectionner un représentant"
        // et le filtre calendrier individuel.
        axios.get(`${API_URL}/api/employees?include_owners=true&include_representants_users=true`),
        axios.get(`${API_URL}/api/conges`),
        ...indispoPromises,
        ...rdvPromises,
      ]);

      const indispoResults = rest.slice(0, monthsToFetch.length);
      const rdvResults = rest.slice(monthsToFetch.length);

      let allIndispos = [];
      for (const res of indispoResults) {
        if (res.status === 200) allIndispos = allIndispos.concat(res.data.indisponibilites || []);
      }
      let allRdvs = [];
      for (const res of rdvResults) {
        if (res.status === 200) allRdvs = allRdvs.concat(res.data.rendez_vous || []);
      }
      // Dédupliquage par id
      setIndisponibilites([...new Map(allIndispos.map(i => [i.id, i])).values()]);
      setRendezVous([...new Map(allRdvs.map(r => [r.id, r])).values()]);
      if (empRes.status === 200) setEmployees(empRes.data.employees || []);
      if (congesRes.status === 200) setConges(congesRes.data.conges || []);
      
      // Charger les événements personnels de l'utilisateur
      if (myEmployeeId) {
        try {
          const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
          const evtRes = await axios.get(`${API_URL}/api/agenda-perso`, {
            params: { employe_id: myEmployeeId },
            headers: { Authorization: `Bearer ${token}` }
          });
          if (evtRes.status === 200) setEvenements(evtRes.data.events || []);
        } catch (e) { console.error("Error:", e); }
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchDossier = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/crm/dossiers/${dossierId}`);
      if (r.status === 200) setDossier(r.data);
    } catch (e) { console.error(e); }
  };

  // Créer un événement personnel avec invitations
  const handleCreateEvent = async () => {
    if (!newEvent.titre || !newEvent.date || !newEvent.heure_debut) {
      toast.error('Titre, date et heure sont requis');
      return;
    }
    // 🐛 [Phase 99 Feature #6] Ne vérifier les conflits QUE si l'événement bloque MON calendrier
    // (mode_visibilite=moi_seul OU moi_et_invites). Sinon (invites_seulement + je ne suis pas invité),
    // pas de blocage sur MON calendrier.
    const blockeMoi = newEvent.mode_visibilite !== 'invites_seulement';
    if (blockeMoi) {
      const dateStr = newEvent.date;
      const conflictRdv = rendezVous.find(r => r.date === dateStr && r.employe_id === myEmployeeId && (r.heure === newEvent.heure_debut || r.heure_debut === newEvent.heure_debut));
      if (conflictRdv) {
        toast.error(`Conflit: RDV CRM déjà planifié à ${newEvent.heure_debut} (${conflictRdv.client_nom || conflictRdv.titre})`);
        return;
      }
      const conflictEvt = evenements.find(e =>
        e.date === newEvent.date && e.heure_debut === newEvent.heure_debut &&
        // Seuls les événements qui bloquent MON calendrier comptent
        (Array.isArray(e.bloque_calendrier_pour)
          ? e.bloque_calendrier_pour.includes(myEmployeeId)
          : true)  // rétro-compat : ancien événement sans champ = bloque tout le monde
      );
      if (conflictEvt) {
        toast.error(`Conflit: événement "${conflictEvt.titre}" déjà à ${newEvent.heure_debut}`);
        return;
      }
    }
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const res = await axios.post(`${API_URL}/api/agenda-perso`, 
        { ...newEvent, employe_id: myEmployeeId },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        const evt = res.data;
        const nbInvites = evt.invites?.length || 0;
        toast.success(`Événement créé${nbInvites > 0 ? ` — ${nbInvites} invitation(s) envoyée(s)` : ''}`);
        setShowCreateEvent(false);
        setNewEvent({
          titre: '', date: '', heure_debut: '09:00', heure_fin: '10:00', description: '', invites: [], recurrence: 'aucune',
          adresse: '', adresse_lat: null, adresse_lng: null, adresse_place_id: null,
          mode_visibilite: 'moi_et_invites',
          rappels_config: { envoyer_24h: true, envoyer_1h: true, delais_custom_minutes: [] },
        });
        fetchData();
      } else {
        const err = res.data || {};
        toast.error(err.detail || 'Erreur création événement');
      }
    } catch (e) { 
      const err = e.response?.data || {};
      toast.error(err.detail || 'Erreur réseau');
    }
  };

  const toggleInvite = (empId) => {
    setNewEvent(prev => ({
      ...prev,
      invites: prev.invites.includes(empId)
        ? prev.invites.filter(id => id !== empId)
        : [...prev.invites, empId]
    }));
  };

  // Combiner les RDV CRM + événements personnels pour l'affichage
  const getEventsForDate = (dateStr) => {
    const rdvs = rendezVous.filter(r => r.date === dateStr);
    const evts = evenements.filter(e => e.date === dateStr);
    return { rdvs, evts };
  };



  // Vérifie si un employé est indisponible ou en congé à une date donnée
  const isEmployeeUnavailable = (employeeId, dateStr) => {
    if (!dateStr) return null;
    // Check indisponibilités
    const indispo = indisponibilites.find(i =>
      i.representant_id === employeeId && dateStr >= i.date_debut && dateStr <= (i.date_fin || i.date_debut)
    );
    if (indispo) return { type: 'indispo', raison: indispo.raison || 'Indisponible' };
    // Check congés approuvés
    const conge = conges.find(cg =>
      cg.employee_id === employeeId && cg.statut === 'approuve' && dateStr >= cg.date_debut && dateStr <= cg.date_fin
    );
    if (conge) return { type: 'conge', raison: conge.type_conge || 'En congé' };
    return null;
  };

  // RDVs filtrés — chacun voit son propre calendrier, ou celui sélectionné par proprio/adjoint
  const rendezVousFiltered = rendezVous.filter(r => {
    if (!canViewOthers) return r.employe_id === myEmployeeId;
    if (filtreEmploye && filtreEmploye !== 'tous') return r.employe_id === filtreEmploye;
    return true;
  });

  const navigatePrev = () => {
    if (viewMode === 'week') {
      const nw = new Date(currentWeekStart);
      nw.setDate(nw.getDate() - 7);
      setCurrentWeekStart(nw);
      setCurrentDate(nw);
    } else {
      const nd = new Date(currentDate);
      nd.setMonth(nd.getMonth() - 1);
      setCurrentDate(nd);
    }
  };

  const navigateNext = () => {
    if (viewMode === 'week') {
      const nw = new Date(currentWeekStart);
      nw.setDate(nw.getDate() + 7);
      setCurrentWeekStart(nw);
      setCurrentDate(nw);
    } else {
      const nd = new Date(currentDate);
      nd.setMonth(nd.getMonth() + 1);
      setCurrentDate(nd);
    }
  };

  const navTitle = viewMode === 'week'
    ? (() => {
        const wd = getWeekDays(currentWeekStart);
        const s = wd[0];
        const e = wd[6];
        if (s.getMonth() === e.getMonth()) {
          return `${s.getDate()} – ${e.getDate()} ${MOIS[s.getMonth()]} ${s.getFullYear()}`;
        }
        return `${s.getDate()} ${MOIS[s.getMonth()].slice(0, 4)}. – ${e.getDate()} ${MOIS[e.getMonth()].slice(0, 4)}. ${e.getFullYear()}`;
      })()
    : `${MOIS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  const creerIndisponibilite = async () => {
    if (!nouvelleIndispo.date_debut) {
      toast.error('Veuillez remplir la date');
      return;
    }
    try {
      // Forcer l'indisponibilité pour soi-même
      const myEmp = employees.find(e => e.id === myEmployeeId);
      const myNom = myEmp ? `${myEmp.prenom} ${myEmp.nom}` : '';

      const recurrence = nouvelleIndispo.recurrence || 'aucune';
      const dates = [nouvelleIndispo.date_debut];
      if (recurrence !== 'aucune') {
        const nbOcc = recurrence === 'hebdomadaire' ? 52 : (recurrence === '2semaines' ? 26 : 12);
        let d = new Date(nouvelleIndispo.date_debut + 'T12:00:00');
        for (let i = 1; i < nbOcc; i++) {
          if (recurrence === 'hebdomadaire') d.setDate(d.getDate() + 7);
          else if (recurrence === '2semaines') d.setDate(d.getDate() + 14);
          else if (recurrence === 'mensuel') d.setMonth(d.getMonth() + 1);
          dates.push(toLocalDateStr(d));
        }
      }
      for (const dateStr of dates) {
        const payload = {
          ...nouvelleIndispo,
          representant_id: myEmployeeId,
          representant_nom: myNom,
          date_debut: dateStr,
          date_fin: dateStr,
        };
        await axios.post(`${API_URL}/api/crm/indisponibilites`, payload);
      }
      toast.success(`Indisponibilite ajoutee${dates.length > 1 ? ` (${dates.length} occurrences)` : ''}!`);
      setShowIndispo(false);
      fetchData();
    } catch { toast.error('Erreur lors de la creation'); }
  };

  const cedulerSoumission = async () => {
    if (!selectedCreneau || !selectedDate) return;
    try {
      const [h, m] = selectedCreneau.split(':');
      const heureFin = `${(parseInt(h) + 1).toString().padStart(2, '0')}:${m}`;
      const client = await axios.get(`${API_URL}/api/crm/clients/${dossier.client_id}`).then(r => r.data);
      const infosEntreprise = await axios.get(`${API_URL}/api/entreprise/infos`).then(r => r.data);
      const emp = employees.find(e => e.id === (dossier.representant_id || employees[0]?.id));
      const rdvData = {
        type_rdv: 'soumission',
        titre: `Soumission - ${dossier.client_nom}`,
        dossier_id: dossier.id, dossier_nom: dossier.nom_dossier,
        client_id: dossier.client_id, client_nom: dossier.client_nom,
        client_email: client.email,
        adresse_travaux: dossier.adresse_travaux, ville_travaux: dossier.ville_travaux,
        code_postal_travaux: dossier.code_postal_travaux,
        employe_id: dossier.representant_id || employees[0]?.id,
        employe_nom: dossier.representant_nom || `${employees[0]?.prenom} ${employees[0]?.nom}`,
        date: selectedDate.toISOString().split('T')[0],
        heure_debut: selectedCreneau, heure_fin: heureFin,
      };
      setPendingRdvData(rdvData);
      const tplRes = await axios.get(`${API_URL}/api/messages-templates?categorie=rendez_vous`);
      const tplData = tplRes.data;
      const template = (tplData.templates || [])[0];
      const vars = {
        client_nom: dossier.client_nom || '',
        // formatLocalTime skipped: cette ligne formate la date pour la DB (YYYY-MM-DD), pas un affichage. La timezone locale du navigateur est utilisée intentionnellement pour matcher le calendrier visible côté utilisateur.
        date_rdv: selectedDate.toLocaleDateString('fr-CA'),
        heure_rdv: selectedCreneau,
        representant: rdvData.employe_nom || '',
        telephone_representant: emp?.telephone || '',
        adresse_travaux: dossier.adresse_travaux || '',
        nom_entreprise: infosEntreprise?.nom_entreprise || '',
        telephone_entreprise: infosEntreprise?.telephone || '',
        adresse_entreprise: infosEntreprise?.adresse || '',
        numero_rbq: infosEntreprise?.numero_rbq ? `RBQ : ${infosEntreprise.numero_rbq}` : '',
        logo_email: '',
      };
      let sujet, contenu;
      if (template) {
        sujet = template.sujet || 'Confirmation de votre rendez-vous';
        contenu = template.contenu || '';
        Object.entries(vars).forEach(([k, v]) => {
          sujet = sujet.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
          contenu = contenu.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        });
        contenu = contenu.replace(/\{lien_confirmation\}/g, '(Boutons de confirmation ajoutés automatiquement)');
        contenu = contenu.replace(/\{[a-z_]+\}/g, '');
        sujet = sujet.replace(/\{[a-z_]+\}/g, '');
      } else {
        sujet = `Confirmation de votre rendez-vous - ${vars.nom_entreprise}`;
        contenu = `Bonjour ${vars.client_nom},\n\nVotre rendez-vous pour l'estimation est confirmé!\n\nDate: ${vars.date_rdv}\nHeure: ${vars.heure_rdv}\nAvec: ${vars.representant} (${vars.telephone_representant})\n\nAu plaisir de vous rencontrer!\n\n${vars.nom_entreprise}`;
      }
      setEmailPreview({ sujet, contenu, email: client.email || '', _preview: false });
      setShowCeduler(false);
      setShowEmailPreview(true);
    } catch { toast.error('Erreur lors de la préparation'); }
  };

  const envoyerRdvAvecEmail = async () => {
    if (!pendingRdvData) return;
    setSendingEmail(true);
    try {
      const r = await axios.post(`${API_URL}/api/crm/rendez-vous`, {
        ...pendingRdvData, 
        email_sujet: emailPreview.sujet, 
        email_contenu: emailPreview.contenu,
        custom_email: emailPreview.email  // ✅ FIX : Envoyer l'email modifié pour les tests
      });
      if (r.status !== 200) throw new Error();
      toast.success('Soumission cédulée! Email de confirmation envoyé.');
      setShowEmailPreview(false);
      setPendingRdvData(null);
      setSelectedCreneau(null);
      fetchData();
    } catch { toast.error('Erreur lors de l\'envoi'); }
    finally { setSendingEmail(false); }
  };

  const cedulerAutreRdv = async () => {
    if (!nouveauRdv.employe_id || !nouveauRdv.titre || !nouveauRdv.date || !nouveauRdv.heure_debut) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      const [h, m] = nouveauRdv.heure_debut.split(':');
      const heureFin = `${(parseInt(h) + 1).toString().padStart(2, '0')}:${m}`;
      const r = await axios.post(`${API_URL}/api/crm/rendez-vous`, {
        ...nouveauRdv, heure_fin: heureFin
      });
      if (r.status !== 200) throw new Error();
      toast.success('Événement créé!');
      setShowAutreRdv(false);
      setNouveauRdv({ type_rdv: 'autre', titre: '', employe_id: '', employe_nom: '', date: '', heure_debut: '09:00', notes: '' });
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const supprimerRdv = async (rdvId) => {
    if (!window.confirm('Supprimer ce rendez-vous?')) return;
    try {
      await axios.delete(`${API_URL}/api/crm/rendez-vous/${rdvId}`);
      toast.success('Rendez-vous supprimé!');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const modifierRdv = async () => {
    if (!rdvAModifier) return;
    try {
      const r = await axios.put(`${API_URL}/api/crm/rendez-vous/${rdvAModifier.id}`, rdvAModifier);
      if (r.status !== 200) throw new Error();
      toast.success('Rendez-vous modifié!');
      setShowModifierRdv(false);
      setRdvAModifier(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const creneauEstDisponible = (date, heure) => {
    const dateStr = toLocalDateStr(date);
    const rdvsAVerifier = filtreEmploye === 'tous' ? rendezVous : rendezVous.filter(r => r.employe_id === filtreEmploye);
    return !rdvsAVerifier.some(r => {
      if (r.date !== dateStr) return false;
      if (r.type_rdv === 'soumission') {
        const [h, m] = r.heure_debut.split(':').map(Number);
        const min = h * 60 + m;
        const [ht, mt] = heure.split(':').map(Number);
        const minT = ht * 60 + mt;
        return minT >= min && minT < min + 30;
      }
      return r.heure_debut === heure;
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  };

  const getRdvForDate = (date) => {
    if (!date) return [];
    const dateStr = toLocalDateStr(date);
    return rendezVousFiltered.filter(r => r.date === dateStr);
  };

  const days = getDaysInMonth(currentDate);
  const creneaux = genererCreneaux();
  const now = new Date();
  const totalWeekHeight = (HOURS_END - HOURS_START) * HOUR_HEIGHT;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowTop = ((nowMinutes - HOURS_START * 60) / 60) * HOUR_HEIGHT;

  // ─── Week View Component ───────────────────────────────────────────────
  const renderWeekView = () => {
    const weekDays = getWeekDays(currentWeekStart);

    return (
      <Card data-testid="week-view-container">
        <CardContent className="p-0 overflow-x-auto">
          {/* Sticky header avec les jours */}
          <div
            className="grid border-b bg-white sticky top-0 z-20"
            style={{ gridTemplateColumns: '52px repeat(7, minmax(80px, 1fr))', minWidth: '620px' }}
          >
            <div className="border-r bg-gray-50" />
            {weekDays.map((d, i) => {
              const isToday = d.toDateString() === now.toDateString();
              const dateStr = toLocalDateStr(d);
              const count = rendezVousFiltered.filter(r => r.date === dateStr).length;
              const hasIndispo = indisponibilites.some(ind => dateStr >= ind.date_debut && dateStr <= (ind.date_fin || ind.date_debut));
              return (
                <div
                  key={i}
                  className={`border-l px-1 pt-2 pb-1 text-center cursor-pointer transition-colors hover:bg-gray-50 ${isToday ? 'bg-blue-50' : ''}`}
                  onClick={() => setSelectedDate(d)}
                  data-testid={`week-day-header-${i}`}
                >
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">{JOURS_SEMAINE[i]}</div>
                  <div className={`text-base font-bold mx-auto w-8 h-8 flex items-center justify-center rounded-full mt-0.5 ${isToday ? 'bg-blue-600 text-white' : 'text-gray-900 hover:bg-gray-200'}`}>
                    {d.getDate()}
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-0.5 min-h-[14px]">
                    {count > 0 && (
                      <span className="text-[10px] font-semibold text-blue-600">{count} RDV</span>
                    )}
                    {hasIndispo && (
                      <span className="text-[10px] text-red-500">Indispo</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Grille horaire scrollable */}
          <div ref={scrollRef} className="overflow-y-auto overflow-x-auto" style={{ maxHeight: '620px' }}>
            <div className="relative" style={{ height: totalWeekHeight, minWidth: '620px' }}>

              {/* Lignes d'heures en fond */}
              {Array.from({ length: HOURS_END - HOURS_START }, (_, i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-t border-gray-100"
                  style={{ top: i * HOUR_HEIGHT }}
                />
              ))}
              {/* Lignes demi-heures */}
              {Array.from({ length: HOURS_END - HOURS_START }, (_, i) => (
                <div
                  key={`half-${i}`}
                  className="absolute left-0 right-0 border-t border-gray-50"
                  style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
                />
              ))}

              {/* Colonne des heures */}
              <div
                className="absolute top-0 left-0 bottom-0 border-r bg-white z-10"
                style={{ width: 52 }}
              >
                {Array.from({ length: HOURS_END - HOURS_START }, (_, i) => (
                  <div
                    key={i}
                    className="absolute w-full flex items-start justify-end pr-2"
                    style={{ top: i * HOUR_HEIGHT - 9, height: HOUR_HEIGHT }}
                  >
                    <span className="text-[11px] text-gray-400 leading-none font-medium">
                      {HOURS_START + i}h
                    </span>
                  </div>
                ))}
              </div>

              {/* Colonnes des jours */}
              <div
                className="absolute top-0 right-0 bottom-0"
                style={{ left: 52, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}
              >
                {weekDays.map((d, dayIdx) => {
                  const dateStr = toLocalDateStr(d);
                  const dayRdvs = rendezVousFiltered.filter(r => r.date === dateStr);
                  const isToday = d.toDateString() === now.toDateString();
                  const hasIndispo = indisponibilites.some(ind => dateStr >= ind.date_debut && dateStr <= (ind.date_fin || ind.date_debut));
                  const { layout, totalCols } = getDayEventColumns(dayRdvs);

                  return (
                    <div
                      key={dayIdx}
                      data-testid={`week-day-col-${dayIdx}`}
                      className={`relative border-l h-full ${dossierId ? 'cursor-pointer' : 'cursor-default'} ${isToday ? 'bg-blue-50/20' : hasIndispo ? 'bg-red-50/30' : ''}`}
                      style={{ height: totalWeekHeight }}
                      onClick={(e) => {
                        if (e.target !== e.currentTarget) return;
                        // Hors flow dossier : on ne crée plus par clic → utiliser le bouton "Créer un Événement"
                        if (!dossierId) {
                          setSelectedDate(d);
                          return;
                        }
                        const rect = e.currentTarget.getBoundingClientRect();
                        const scrollTop = scrollRef.current?.scrollTop || 0;
                        const y = e.clientY - rect.top + scrollTop;
                        const totalMins = Math.floor((y / HOUR_HEIGHT) * 60) + HOURS_START * 60;
                        const h = Math.floor(totalMins / 60);
                        const m = Math.round((totalMins % 60) / 15) * 15;
                        const safeH = Math.max(HOURS_START, Math.min(HOURS_END - 1, h));
                        const safeM = m >= 60 ? 0 : m;
                        const timeStr = `${String(safeH).padStart(2, '0')}:${String(safeM).padStart(2, '0')}`;
                        setSelectedDate(d);
                        setSelectedCreneau(timeStr);
                        setNouveauRdv(prev => ({ ...prev, date: dateStr, heure_debut: timeStr }));
                        setShowCeduler(true);
                      }}
                    >
                      {/* Événements du jour */}
                      {dayRdvs.map(rdv => {
                        const startMin = timeToMinutes(rdv.heure_debut);
                        const endMin = timeToMinutes(rdv.heure_fin || addOneHour(rdv.heure_debut));
                        const top = Math.max(0, ((startMin - HOURS_START * 60) / 60) * HOUR_HEIGHT);
                        const height = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 22);
                        const col = layout[rdv.id] || 0;
                        const pad = 3;
                        const colW = totalCols > 1 ? (100 - pad) / totalCols : 94;
                        const colL = totalCols > 1 ? (col / totalCols) * (100 - pad) + 1 : 3;
                        const color = getEmployeeColor(rdv.employe_id);
                        const isSoumission = rdv.type_rdv === 'soumission';

                        return (
                          <div
                            key={rdv.id}
                            data-testid={`week-event-${rdv.id}`}
                            className="absolute rounded overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow z-10"
                            style={{
                              top: top + 2,
                              height: height - 3,
                              left: `${colL}%`,
                              width: `${colW}%`,
                              backgroundColor: color,
                              border: `1.5px solid ${color}dd`,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRdvDetail(rdv);
                            }}
                          >
                            {/* Barre latérale colorée pour soumissions */}
                            {isSoumission && (
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-white/40" />
                            )}
                            <div className="h-full px-1.5 py-0.5 flex flex-col text-white overflow-hidden">
                              <div className="text-[10px] font-bold leading-tight truncate">
                                {rdv.titre}
                              </div>
                              {height > 32 && (
                                <div className="text-[9px] opacity-85 leading-tight truncate">
                                  {rdv.heure_debut} – {rdv.heure_fin}
                                </div>
                              )}
                              {height > 48 && rdv.employe_nom && (
                                <div className="text-[9px] opacity-70 truncate">{rdv.employe_nom}</div>
                              )}
                              {isSoumission && rdv.confirme_par_client && height > 40 && (
                                <div className="text-[8px] mt-auto opacity-90">✓ Confirmé</div>
                              )}
                            </div>
                            {/* Bouton supprimer */}
                            <button
                              className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/30 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center text-[11px] font-bold transition-opacity z-20 hover:bg-red-600"
                              onClick={(e) => { e.stopPropagation(); supprimerRdv(rdv.id); }}
                              title="Supprimer"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}

                      {/* Indicateur heure actuelle */}
                      {isToday && nowTop >= 0 && nowTop <= totalWeekHeight && (
                        <div
                          className="absolute left-0 right-0 z-20 pointer-events-none"
                          style={{ top: nowTop }}
                        >
                          <div className="relative flex items-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1.5 flex-shrink-0" />
                            <div className="flex-1 border-t-2 border-red-500" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Légende */}
          <div className="px-3 py-2 border-t bg-gray-50 flex flex-wrap items-center gap-3 text-[10px] text-gray-500">
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500" /> Heure actuelle
            </span>
            {dossierId ? (
              <span>Cliquer sur un créneau vide pour céduler la soumission</span>
            ) : (
              <span>Utilisez le bouton « Créer un Événement » pour ajouter un événement</span>
            )}
            <span>Cliquer sur un événement pour voir les détails</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Calendrier des Rendez-vous</h1>
          <p className="text-gray-600 mt-1 text-sm">
            {dossierId && dossier ? `Céduler pour: ${dossier.client_nom}` : 'Gérez tous vos rendez-vous'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!dossierId && (
            <Button onClick={() => { setNewEvent(p => ({ ...p, date: selectedDate ? toLocalDateStr(selectedDate) : '' })); setShowCreateEvent(true); }} className="bg-green-600 hover:bg-green-700 text-sm" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Créer un Événement
            </Button>
          )}
          {!canViewOthers ? (
            <div className="text-xs text-gray-400 flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" />
              Mon calendrier
            </div>
          ) : (
            <Select value={filtreEmploye} onValueChange={setFiltreEmploye}>
              <SelectTrigger className="w-auto min-w-[140px] h-8 text-xs border-dashed border-gray-300 bg-transparent" data-testid="filtre-employe-select">
                <SelectValue placeholder="Mon calendrier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">
                  <span className="font-medium">📅 Tous les calendriers</span>
                </SelectItem>
                {myEmployeeId && (
                  <SelectItem value={myEmployeeId}>
                    <span className="font-medium">Mon calendrier</span>
                  </SelectItem>
                )}
                {/* 🐛 [Phase 99 Bug #1] Groupement en 3 sections : Admin / Terrain / Sous-traitants.
                    Auparavant : filtre excluait tous les type_employe='terrain' sauf installateur. */}
                {(() => {
                  const ROLES_ADMIN_LC = new Set([
                    'proprietaire', 'representant', 'representante', 'admin',
                    'adjoint_admin', 'adjoint', 'charge_projet', 'directeur',
                  ]);
                  const ROLES_TERRAIN_LC = new Set([
                    'chef_equipe', 'peintre', 'installateur', 'installateur_lmds',
                    'terrain', 'apprenti',
                  ]);
                  const ROLES_ST_LC = new Set([
                    'sous_traitant', 'sous-traitant', 'st',
                  ]);
                  const groupsRaw = { admin: [], terrain: [], st: [] };
                  const seen = new Set();
                  for (const emp of employees) {
                    if (!emp || !emp.id || emp.id === myEmployeeId) continue;
                    if (emp.est_actif === false || emp.actif === false) continue;
                    if (seen.has(emp.id)) continue;
                    seen.add(emp.id);
                    const role = (emp.role || emp.type_employe || '').toString().toLowerCase();
                    const typeEmp = (emp.type_employe || '').toString().toLowerCase();
                    let bucket = null;
                    if (ROLES_ADMIN_LC.has(role) || typeEmp === 'admin' || isRepresentantCandidate(emp)) {
                      bucket = 'admin';
                    } else if (ROLES_ST_LC.has(role) || typeEmp === 'sous_traitant' || typeEmp === 'sous-traitant') {
                      bucket = 'st';
                    } else if (ROLES_TERRAIN_LC.has(role) || typeEmp === 'terrain') {
                      bucket = 'terrain';
                    } else {
                      // Fallback : classer dans Terrain par défaut si role inconnu (évite exclusion)
                      bucket = 'terrain';
                    }
                    groupsRaw[bucket].push(emp);
                  }
                  const sortByName = (a, b) => `${a.prenom || ''} ${a.nom || ''}`.trim().localeCompare(`${b.prenom || ''} ${b.nom || ''}`.trim(), 'fr', { sensitivity: 'base' });
                  Object.values(groupsRaw).forEach(arr => arr.sort(sortByName));
                  const SECTIONS = [
                    { key: 'admin', label: '👨‍💼 Admin', arr: groupsRaw.admin },
                    { key: 'terrain', label: '🔨 Terrain', arr: groupsRaw.terrain },
                    { key: 'st', label: '🤝 Sous-traitants', arr: groupsRaw.st },
                  ];
                  return SECTIONS.filter(s => s.arr.length > 0).map((sec, idx) => (
                    <SelectGroup key={sec.key} data-testid={`filtre-employe-group-${sec.key}`}>
                      {idx > 0 && <SelectSeparator />}
                      <SelectLabel className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                        {sec.label}
                      </SelectLabel>
                      {sec.arr.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id} data-testid={`filtre-employe-item-${emp.id}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getEmployeeColor(emp.id) }} />
                            {emp.prenom} {emp.nom}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ));
                })()}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => setShowIndispo(true)} variant="outline" className="text-sm" data-testid="add-indispo-btn">
            <X className="w-4 h-4 mr-2" />
            Indisponibilité
          </Button>
          <div className="flex border rounded-lg overflow-hidden" data-testid="view-mode-toggle">
            <button
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              onClick={() => handleViewModeChange('month')}
            >
              Mois
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'week' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              onClick={() => handleViewModeChange('week')}
            >
              Semaine
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <Card className="mb-4">
        <CardContent className="pt-4 pb-4">
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={navigatePrev} data-testid="nav-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900" data-testid="nav-title">{navTitle}</h2>
              {viewMode === 'week' && (
                <button
                  className="text-xs text-blue-600 hover:underline mt-0.5"
                  onClick={() => {
                    const ws = getWeekStart(new Date());
                    setCurrentWeekStart(ws);
                    setCurrentDate(ws);
                  }}
                >
                  Semaine actuelle
                </button>
              )}
            </div>
            <Button variant="outline" onClick={navigateNext} data-testid="nav-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vue Semaine (pleine largeur) */}
      {viewMode === 'week' && renderWeekView()}

      {/* Vue Mois (layout 2/3 + 1/3) */}
      {viewMode === 'month' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardContent className="pt-6">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {JOURS.map((jour) => (
                  <div key={jour} className="text-center font-semibold text-gray-600 text-xs sm:text-sm">
                    {jour.slice(0, 1)}<span className="hidden sm:inline">{jour.slice(1)}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  if (!day) return <div key={`empty-${index}`} className="aspect-square" />;
                  const isToday = day.toDateString() === new Date().toDateString();
                  const rdvs = getRdvForDate(day);
                  const dateStr = toLocalDateStr(day);
                  const dayIndispos = indisponibilites.filter(ind => dateStr >= ind.date_debut && dateStr <= (ind.date_fin || ind.date_debut));
                  return (
                    <div
                      key={dateStr}
                      data-testid={`month-day-${day.getDate()}`}
                      className={`aspect-square border rounded-lg p-1 cursor-pointer transition-all overflow-hidden flex flex-col items-center justify-start ${
                        dayIndispos.length > 0 ? 'bg-red-50 border-red-300' : isToday ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                      } ${selectedDate && day.toDateString() === selectedDate.toDateString() ? 'ring-2 ring-blue-500' : ''}`}
                      onClick={() => setSelectedDate(day)}
                      title={dayIndispos.length > 0 ? `Indisponible: ${dayIndispos.map(i => i.representant_nom).join(', ')}` : ''}
                    >
                      <p className={`text-xs font-semibold leading-tight ${isToday ? 'text-blue-600' : dayIndispos.length > 0 ? 'text-red-600' : ''}`}>
                        {day.getDate()}
                      </p>
                      {rdvs.length > 0 && (
                        <span className="mt-0.5 w-full flex justify-center">
                          <span className="inline-flex items-center justify-center bg-blue-500 text-white rounded-full text-[10px] font-bold leading-none w-4 h-4">{rdvs.length}</span>
                        </span>
                      )}
                      {evenements.filter(e => e.date === dateStr).length > 0 && (
                        <span className="mt-0.5 w-full flex justify-center">
                          <span className="inline-flex items-center justify-center bg-green-500 text-white rounded-full text-[10px] font-bold leading-none w-4 h-4">{evenements.filter(e => e.date === dateStr).length}</span>
                        </span>
                      )}
                      {dayIndispos.length > 0 && rdvs.length === 0 && (
                        <X className="w-3 h-3 text-red-400 mt-0.5" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Panel détail du jour sélectionné */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {selectedDate
                  ? formatLocalTime(selectedDate.toISOString(), { dateOnly: true, weekday: 'long', withoutYear: true })
                  : 'Sélectionnez une date'}
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {!selectedDate ? (
                <p className="text-gray-500 text-sm text-center py-8">Cliquez sur une date pour voir les créneaux</p>
              ) : (
                <div className="space-y-2">
                  {getRdvForDate(selectedDate).map((rdv) => {
                    const color = getEmployeeColor(rdv.employe_id);
                    const colorLight = getEmployeeColorLight(rdv.employe_id);
                    return (
                      <div key={rdv.id} className="p-3 border-l-4 rounded relative group" style={{ borderLeftColor: color, backgroundColor: colorLight }}>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setRdvAModifier(rdv); setShowModifierRdv(true); }}>✏️</Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => supprimerRdv(rdv.id)}>🗑️</Button>
                        </div>
                        <div className="flex justify-between items-start mb-1 pr-16">
                          <p className="font-semibold text-sm">{rdv.titre}</p>
                          <Badge className={rdv.type_rdv === 'soumission' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                            {rdv.type_rdv === 'soumission' ? 'Soumission' : 'Autre'}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600"><Clock className="w-3 h-3 inline mr-1" />{rdv.heure_debut} - {rdv.heure_fin}</p>
                        <p className="text-xs text-gray-600">Avec: {rdv.employe_nom}</p>
                        {rdv.adresse_travaux && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-gray-600">{rdv.adresse_travaux}</p>
                            <p className="text-xs text-gray-600">{rdv.ville_travaux}, {rdv.code_postal_travaux}</p>
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rdv.adresse_travaux + ' ' + rdv.ville_travaux)}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center mt-1">
                              Ouvrir dans Google Maps
                            </a>
                          </div>
                        )}
                        {rdv.client_telephone && (
                          <a href={`tel:${rdv.client_telephone}`} className="flex items-center gap-1.5 mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium">
                            <Phone className="w-3 h-3" /> {rdv.client_telephone}
                          </a>
                        )}
                        {rdv.type_rdv === 'soumission' && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge className={rdv.confirme_par_client ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {rdv.confirme_par_client ? 'Confirmé' : 'En attente'}
                            </Badge>
                            {!rdv.visite_effectuee ? (
                              <button
                                data-testid={`btn-visite-effectuee-carte-${rdv.id}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm('Marquer cette visite comme effectuée ?\n\nLe dossier passera en "Soum. à faire".')) return;
                                  if (!window.confirm('Confirmation finale — êtes-vous certain ?')) return;
                                  try {
                                    const res = await axios.patch(`${API_URL}/api/crm/rendez-vous/${rdv.id}/visite-effectuee`);
                                    if (res.status === 200) {
                                      toast.success('Visite effectuée — dossier en "Soum. à faire"');
                                      fetchData();
                                    }
                                  } catch { toast.error('Erreur'); }
                                }}
                                className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded-full font-medium transition-colors"
                              >
                                ✓ Visite effectuée
                              </button>
                            ) : (
                              <Badge className="bg-indigo-100 text-indigo-800">Visite effectuée</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Événements personnels */}
                  {selectedDate && evenements.filter(e => e.date === toLocalDateStr(selectedDate)).map(evt => (
                    <div key={evt.id} className="p-3 border-l-4 border-green-500 bg-green-50 rounded">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p className="font-semibold text-sm">{evt.titre}</p>
                          {evt.recurrence && (
                            <span className="text-[10px] text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                              {evt.recurrence === 'hebdomadaire' ? 'Chaque semaine' : evt.recurrence === '2semaines' ? 'Chaque 2 sem.' : 'Chaque mois'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className="bg-green-100 text-green-800">Evenement</Badge>
                          <button
                            className="text-red-400 hover:text-red-600 p-0.5"
                            title="Supprimer"
                            onClick={async () => {
                              const hasGroup = !!evt.recurrence_group_id;
                              const deleteGroup = hasGroup ? window.confirm('Cet evenement fait partie d\'une serie recurrente.\n\nCliquez OK pour supprimer TOUS les evenements futurs de cette serie.\nCliquez Annuler pour supprimer seulement celui-ci.') : false;
                              if (!hasGroup && !window.confirm('Supprimer cet evenement ?')) return;
                              try {
                                const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
                                const url = `${API_URL}/api/agenda-perso/${evt.id}${deleteGroup ? '?delete_group=true' : ''}`;
                                const r = await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
                                if (r.status === 200) {
                                  const d = r.data;
                                  toast.success(d.deleted_count > 1 ? `${d.deleted_count} evenements supprimes` : 'Evenement supprime');
                                  fetchData();
                                } else toast.error('Erreur suppression');
                              } catch { toast.error('Erreur reseau'); }
                            }}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600"><Clock className="w-3 h-3 inline mr-1" />{evt.heure_debut} - {evt.heure_fin}</p>
                      {evt.description && <p className="text-xs text-gray-500 mt-1">{evt.description}</p>}
                      {evt.invites?.length > 0 && (
                        <div className="mt-1.5">
                          <p className="text-xs text-gray-500">Invites: {evt.invites.map(i => `${i.nom} (${i.statut === 'accepte' ? '✅' : i.statut === 'refuse' ? '❌' : '⏳'})`).join(', ')}</p>
                        </div>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">Cree par {evt.created_by_nom}</p>
                    </div>
                  ))}
                  <div className="border-t pt-3 mt-3">
                    {dossierId ? (
                      <>
                        <p className="font-semibold text-sm mb-2">Créneaux disponibles:</p>
                        <div className="grid grid-cols-2 gap-1">
                          {creneaux.map((creneau) => {
                            const dispo = creneauEstDisponible(selectedDate, creneau);
                            return (
                              <Button key={creneau} size="sm" variant={dispo ? 'outline' : 'ghost'}
                                className={`text-xs ${!dispo ? 'opacity-30 cursor-not-allowed' : ''}`}
                                disabled={!dispo}
                                onClick={() => {
                                  setSelectedCreneau(creneau);
                                  setShowCeduler(true);
                                }}
                              >
                                {creneau}
                              </Button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          setNewEvent(p => ({ ...p, date: selectedDate ? toLocalDateStr(selectedDate) : '' }));
                          setShowCreateEvent(true);
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        data-testid="create-event-from-panel-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créer un Événement ce jour
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── MODALS ─────────────────────────────────────────────────────── */}

      {/* Modal Détail RDV (vue hebdomadaire) */}
      <Dialog open={!!rdvDetail} onOpenChange={(open) => { if (!open) setRdvDetail(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <span>{rdvDetail?.titre}</span>
              <Badge className={rdvDetail?.type_rdv === 'soumission' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                {rdvDetail?.type_rdv === 'soumission' ? 'Soumission' : 'Autre'}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          {rdvDetail && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Clock className="w-4 h-4 text-blue-500" />
                <span>{rdvDetail.date} · {rdvDetail.heure_debut} - {rdvDetail.heure_fin}</span>
              </div>
              {rdvDetail.employe_nom && (
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Avec:</span> {rdvDetail.employe_nom}
                </div>
              )}
              {rdvDetail.client_telephone && (
                <a href={`tel:${rdvDetail.client_telephone}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
                  <Phone className="w-4 h-4" />
                  {rdvDetail.client_telephone}
                </a>
              )}
              {rdvDetail.adresse_travaux && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                  <p className="text-gray-700">{rdvDetail.adresse_travaux}</p>
                  <p className="text-gray-600">{rdvDetail.ville_travaux}{rdvDetail.code_postal_travaux ? `, ${rdvDetail.code_postal_travaux}` : ''}</p>
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rdvDetail.adresse_travaux + ' ' + (rdvDetail.ville_travaux || ''))}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center text-xs mt-1">
                    Ouvrir dans Google Maps
                  </a>
                </div>
              )}
              {rdvDetail.type_rdv === 'soumission' && (
                <div className="flex flex-wrap gap-2">
                  <Badge className={rdvDetail.confirme_par_client ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {rdvDetail.confirme_par_client ? 'Confirmé par le client' : 'En attente de confirmation'}
                  </Badge>
                  {rdvDetail.visite_effectuee && (
                    <Badge className="bg-indigo-100 text-indigo-800">Visite effectuée</Badge>
                  )}
                </div>
              )}
              {rdvDetail.notes && (
                <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-xs text-gray-500 mb-1">Notes</p>
                  {rdvDetail.notes}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                {rdvDetail.type_rdv === 'soumission' && !rdvDetail.visite_effectuee && (
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="btn-visite-effectuee"
                    onClick={async () => {
                      if (!window.confirm('Marquer cette visite comme effectuée ?\n\nLe dossier passera en "Soum. à faire".')) return;
                      if (!window.confirm('Confirmation finale — êtes-vous certain ?')) return;
                      try {
                        const res = await axios.patch(`${API_URL}/api/crm/rendez-vous/${rdvDetail.id}/visite-effectuee`);
                        if (res.status !== 200) throw new Error();
                        toast.success('Visite effectuée — dossier en "Soum. à faire"');
                        setRdvDetail(null);
                        fetchData();
                      } catch { toast.error('Erreur lors de la mise à jour'); }
                    }}
                  >
                    ✓ Visite effectuée
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setRdvAModifier(rdvDetail); setShowModifierRdv(true); setRdvDetail(null); }}>
                  Modifier
                </Button>
                <Button size="sm" variant="destructive" onClick={() => { supprimerRdv(rdvDetail.id); setRdvDetail(null); }}>
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Indisponibilité */}
      <Dialog open={showIndispo} onOpenChange={setShowIndispo}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter une Indisponibilite</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="text-slate-600">Indisponibilite pour : <strong>{employees.find(e => e.id === myEmployeeId)?.prenom || ''} {employees.find(e => e.id === myEmployeeId)?.nom || 'Moi'}</strong></p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date Debut</Label><Input type="date" value={nouvelleIndispo.date_debut} onChange={e => setNouvelleIndispo({ ...nouvelleIndispo, date_debut: e.target.value })} /></div>
              <div><Label>Date Fin</Label><Input type="date" value={nouvelleIndispo.date_fin} onChange={e => setNouvelleIndispo({ ...nouvelleIndispo, date_fin: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Heure Debut (opt.)</Label><Input type="time" value={nouvelleIndispo.heure_debut || ''} onChange={e => setNouvelleIndispo({ ...nouvelleIndispo, heure_debut: e.target.value, toute_journee: !e.target.value })} /></div>
              <div><Label>Heure Fin (opt.)</Label><Input type="time" value={nouvelleIndispo.heure_fin || ''} onChange={e => setNouvelleIndispo({ ...nouvelleIndispo, heure_fin: e.target.value })} /></div>
            </div>
            <div><Label>Raison</Label><Input value={nouvelleIndispo.raison} onChange={e => setNouvelleIndispo({ ...nouvelleIndispo, raison: e.target.value })} /></div>
            <div>
              <Label>Recurrence</Label>
              <select
                value={nouvelleIndispo.recurrence}
                onChange={e => setNouvelleIndispo({ ...nouvelleIndispo, recurrence: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                data-testid="indispo-recurrence"
              >
                <option value="aucune">Aucune (unique)</option>
                <option value="hebdomadaire">Chaque semaine</option>
                <option value="2semaines">Chaque 2 semaines</option>
                <option value="mensuel">Chaque mois</option>
              </select>
              {nouvelleIndispo.recurrence !== 'aucune' && (
                <p className="text-xs text-blue-600 mt-1">
                  {nouvelleIndispo.recurrence === 'hebdomadaire' ? '52' : nouvelleIndispo.recurrence === '2semaines' ? '26' : '12'} occurrences seront creees (~1 an)
                </p>
              )}
            </div>
            <p className="text-sm text-gray-500">Laissez les heures vides pour une indisponibilite toute la journee</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowIndispo(false)}>Annuler</Button>
              <Button onClick={creerIndisponibilite}>Creer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Céduler Soumission */}
      <Dialog open={showCeduler} onOpenChange={setShowCeduler}>
        <DialogContent>
          <DialogHeader><DialogTitle>Céduler la Soumission</DialogTitle></DialogHeader>
          {dossier && selectedDate && selectedCreneau && (
            <div className="space-y-4">
              {/* ✅ NOUVEAU : Sélecteur de représentant */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Représentant assigné *</Label>
                <Select 
                  value={dossier.representant_id || ''} 
                  onValueChange={(val) => {
                    const emp = (employees || []).find(e => e.id === val);
                    if (emp) {
                      setDossier(prev => ({ 
                        ...prev, 
                        representant_id: val, 
                        representant_nom: `${emp.prenom} ${emp.nom}`,
                        employe_id: val,
                        employe_nom: `${emp.prenom} ${emp.nom}`,
                        employe_telephone: emp.telephone || ''
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionner un représentant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(employees || [])
                      .filter(isRepresentantCandidate)
                      .map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.prenom} {emp.nom} ({emp.role || emp.type_employe || 'représentant'})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 p-4 rounded">
                <p className="font-semibold">{dossier.client_nom}</p>
                <p className="text-sm text-gray-600">{dossier.nom_dossier}</p>
                <p className="text-sm font-medium text-blue-700 mt-1">📍 Représentant: {dossier.representant_nom || 'Non assigné'}</p>
                <p className="text-sm text-gray-600 mt-2">{formatLocalDate(selectedDate.toISOString())} à {selectedCreneau}</p>
              </div>
              <p className="text-sm text-gray-600">Un email de confirmation sera préparé pour votre approbation avant l'envoi.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCeduler(false)}>Annuler</Button>
                <Button onClick={cedulerSoumission} className="bg-green-600 hover:bg-green-700">Préparer le courriel de confirmation</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Preview Email — composant partagé (identique dans CreerDossier) */}
      <RdvEmailConfirmationDialog
        open={showEmailPreview}
        onOpenChange={setShowEmailPreview}
        emailData={emailPreview}
        onChange={setEmailPreview}
        onSend={envoyerRdvAvecEmail}
        sending={sendingEmail}
        previewHtml={rdvPreviewHtml}
        loadingPreview={rdvLoadingPreview}
        fetchPreview={rdvFetchPreview}
      />

      {/* Modal Créer Événement */}
      <Dialog open={showAutreRdv} onOpenChange={setShowAutreRdv}>
        <DialogContent>
          <DialogHeader><DialogTitle>Créer un Événement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Titre *</Label><Input placeholder="Ex: Meeting équipe, Entretien camion..." value={nouveauRdv.titre} onChange={e => setNouveauRdv({ ...nouveauRdv, titre: e.target.value })} data-testid="event-titre-input" /></div>
            <div>
              <Label>Employé Assigné *</Label>
              <Select value={nouveauRdv.employe_id || undefined} onValueChange={val => {
                const emp = employees.find(e => e.id === val);
                const unavail = isEmployeeUnavailable(val, nouveauRdv.date);
                if (unavail) toast.warning(`${emp?.prenom} ${emp?.nom} est ${unavail.type === 'conge' ? 'en congé' : 'indisponible'} cette journée (${unavail.raison})`);
                setNouveauRdv({ ...nouveauRdv, employe_id: val, employe_nom: emp ? `${emp.prenom} ${emp.nom}` : '' });
              }}>
                <SelectTrigger data-testid="event-employe-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{employees.map(emp => {
                  const unavail = isEmployeeUnavailable(emp.id, nouveauRdv.date);
                  return (
                    <SelectItem key={emp.id} value={emp.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEmployeeColor(emp.id) }} />
                        {emp.prenom} {emp.nom}
                        {unavail && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full font-medium ml-1">{unavail.type === 'conge' ? 'Congé' : 'Indispo'}</span>}
                      </div>
                    </SelectItem>
                  );
                })}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date *</Label><Input type="date" value={nouveauRdv.date} onChange={e => setNouveauRdv({ ...nouveauRdv, date: e.target.value })} data-testid="event-date-input" /></div>
              <div><Label>Heure *</Label><Input type="time" value={nouveauRdv.heure_debut} onChange={e => setNouveauRdv({ ...nouveauRdv, heure_debut: e.target.value })} data-testid="event-heure-input" /></div>
            </div>
            <div><Label>Notes (opt.)</Label><Textarea placeholder="Notes sur l'événement..." value={nouveauRdv.notes} onChange={e => setNouveauRdv({ ...nouveauRdv, notes: e.target.value })} rows={3} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAutreRdv(false)}>Annuler</Button>
              <Button onClick={cedulerAutreRdv} data-testid="create-event-submit-btn">Créer l'Événement</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Modifier RDV */}
      <Dialog open={showModifierRdv} onOpenChange={setShowModifierRdv}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier le Rendez-vous</DialogTitle></DialogHeader>
          {rdvAModifier && (
            <div className="space-y-4">
              <div><Label>Titre *</Label><Input value={rdvAModifier.titre} onChange={e => setRdvAModifier({ ...rdvAModifier, titre: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date *</Label><Input type="date" value={rdvAModifier.date} onChange={e => setRdvAModifier({ ...rdvAModifier, date: e.target.value })} /></div>
                <div><Label>Heure *</Label><Input type="time" value={rdvAModifier.heure_debut} onChange={e => setRdvAModifier({ ...rdvAModifier, heure_debut: e.target.value })} /></div>
              </div>
              <div>
                <Label>Employé *</Label>
                <Select value={rdvAModifier.employe_id || undefined} onValueChange={val => {
                  const emp = employees.find(e => e.id === val);
                  const unavail = isEmployeeUnavailable(val, rdvAModifier.date);
                  if (unavail) toast.warning(`${emp?.prenom} ${emp?.nom} est ${unavail.type === 'conge' ? 'en congé' : 'indisponible'} cette journée`);
                  setRdvAModifier({ ...rdvAModifier, employe_id: val, employe_nom: emp ? `${emp.prenom} ${emp.nom}` : '' });
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{employees.map(emp => {
                    const unavail = isEmployeeUnavailable(emp.id, rdvAModifier?.date);
                    return (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          {emp.prenom} {emp.nom}
                          {unavail && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full font-medium ml-1">{unavail.type === 'conge' ? 'Congé' : 'Indispo'}</span>}
                        </div>
                      </SelectItem>
                    );
                  })}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowModifierRdv(false); setRdvAModifier(null); }}>Annuler</Button>
                <Button onClick={modifierRdv} data-testid="save-rdv-btn">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Dialog Créer Événement avec invitations ─── */}
      <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
        {/* 🆕 [v13.60.4 — 2026-08-12] Modale scrollable sur mobile.
            - max-h-[90dvh] : limite à 90% de la viewport dynamique (compatible barre URL Safari mobile).
            - overflow-y-auto : scroll interne quand contenu dépasse.
            - flex flex-col : le footer sticky peut s'ancrer en bas.
            Non-régression desktop : max-h ne s'applique que si contenu dépasse.
            Pattern à répliquer globalement dans les autres modales longues (audit v13.62). */}
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-hidden flex flex-col p-0 gap-0" data-testid="dialog-creer-rdv-event">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0"><DialogTitle>Créer un événement</DialogTitle></DialogHeader>
          <div className="space-y-3 px-6 pb-4 flex-1 overflow-y-auto">
            <div>
              <Label>Titre *</Label>
              <Input value={newEvent.titre} onChange={e => setNewEvent(p => ({ ...p, titre: e.target.value }))} placeholder="Réunion, formation, etc." data-testid="event-titre" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={newEvent.date} onChange={e => setNewEvent(p => ({ ...p, date: e.target.value }))} data-testid="event-date" />
              </div>
              <div>
                <Label>Début *</Label>
                <Input type="time" value={newEvent.heure_debut} onChange={e => setNewEvent(p => ({ ...p, heure_debut: e.target.value }))} data-testid="event-debut" />
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="time" value={newEvent.heure_fin} onChange={e => setNewEvent(p => ({ ...p, heure_fin: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Recurrence</Label>
              <select
                value={newEvent.recurrence}
                onChange={e => setNewEvent(p => ({ ...p, recurrence: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                data-testid="event-recurrence"
              >
                <option value="aucune">Aucune (evenement unique)</option>
                <option value="hebdomadaire">Chaque semaine</option>
                <option value="2semaines">Chaque 2 semaines</option>
                <option value="mensuel">Chaque mois</option>
              </select>
              {newEvent.recurrence !== 'aucune' && (
                <p className="text-xs text-blue-600 mt-1">
                  {newEvent.recurrence === 'hebdomadaire' ? '52' : newEvent.recurrence === '2semaines' ? '26' : '12'} occurrences seront creees (~1 an)
                </p>
              )}
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newEvent.description} onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} placeholder="Détails de l'événement..." rows={2} />
            </div>
            <div>
              <Label className="mb-2 block">Participants</Label>
              
              {/* Option "Seulement moi" */}
              <div className="mb-3 p-3 border rounded-lg bg-slate-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="participation-type"
                    checked={newEvent.invites.length === 0}
                    onChange={() => setNewEvent(p => ({ ...p, invites: [] }))}
                    className="rounded"
                  />
                  <span className="font-medium">Seulement moi</span>
                  <span className="text-xs text-slate-500 ml-auto">Événement personnel (aucune invitation)</span>
                </label>
              </div>
              
              {/* Option "Inviter d'autres employés" */}
              <div className="mb-2">
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input 
                    type="radio" 
                    name="participation-type"
                    checked={newEvent.invites.length > 0}
                    onChange={() => {
                      // Ne rien faire, c'est le clic sur une checkbox qui activera ce mode
                    }}
                    className="rounded"
                  />
                  <span className="font-medium">Inviter d'autres employés</span>
                </label>
              </div>
              
              {/* Liste des employés à inviter (affichée toujours, mais visuellement liée à l'option radio) */}
              <div className={`max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2 ml-6 ${newEvent.invites.length === 0 ? 'opacity-50' : ''}`}>
                {employees.filter(e => e.id !== myEmployeeId && e.est_actif !== false).map(emp => (
                  <label key={emp.id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-sm ${newEvent.invites.includes(emp.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={newEvent.invites.includes(emp.id)} onChange={() => toggleInvite(emp.id)} className="rounded" />
                    <span>{emp.prenom} {emp.nom}</span>
                    <span className="text-xs text-slate-400 ml-auto">{emp.type_employe === 'terrain' ? 'Terrain' : emp.role_admin || ''}</span>
                  </label>
                ))}
              </div>
              {newEvent.invites.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">{newEvent.invites.length} personne(s) invitée(s) — un email d'invitation sera envoyé</p>
              )}
            </div>

            {/* 🐛 [Phase 99 Feature #5] Adresse via Google Places Autocomplete */}
            <div>
              <Label>Adresse (optionnel)</Label>
              <GooglePlacesAutocomplete
                value={newEvent.adresse}
                onChange={(val) => setNewEvent(p => ({ ...p, adresse: val }))}
                onPlaceSelected={(details) => setNewEvent(p => ({
                  ...p,
                  adresse: details.adresse_complete,
                  adresse_lat: details.latitude,
                  adresse_lng: details.longitude,
                  adresse_place_id: details.place_id,
                }))}
                placeholder="Ex : 123 rue Principale, Québec"
                dataTestId="event-adresse-autocomplete"
              />
              {newEvent.adresse && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(newEvent.adresse)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center mt-1"
                  data-testid="event-adresse-maps-link"
                >
                  📍 Ouvrir dans Google Maps
                </a>
              )}
            </div>

            {/* 🐛 [Phase 99 Feature #6] Mode visibilité + blocage calendrier */}
            {newEvent.invites.length > 0 && (
              <div>
                <Label className="mb-2 block">Bloque le calendrier de</Label>
                <div className="space-y-2 p-3 border rounded-lg bg-slate-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio" name="mode-visibilite"
                      checked={newEvent.mode_visibilite === 'moi_seul'}
                      onChange={() => setNewEvent(p => ({ ...p, mode_visibilite: 'moi_seul' }))}
                      data-testid="mode-visibilite-moi-seul"
                    />
                    <span className="text-sm"><strong>Moi seulement</strong> — les invités reçoivent une invitation sans bloquer leur agenda</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio" name="mode-visibilite"
                      checked={newEvent.mode_visibilite === 'moi_et_invites'}
                      onChange={() => setNewEvent(p => ({ ...p, mode_visibilite: 'moi_et_invites' }))}
                      data-testid="mode-visibilite-moi-et-invites"
                    />
                    <span className="text-sm"><strong>Moi + les invités</strong> (recommandé)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio" name="mode-visibilite"
                      checked={newEvent.mode_visibilite === 'invites_seulement'}
                      onChange={() => setNewEvent(p => ({ ...p, mode_visibilite: 'invites_seulement' }))}
                      data-testid="mode-visibilite-invites-seulement"
                    />
                    <span className="text-sm"><strong>Les invités seulement</strong> — ne bloque PAS mon calendrier (je vois l&apos;événement en tant qu&apos;organisateur)</span>
                  </label>
                </div>
              </div>
            )}

            {/* 🐛 [Phase 99 Feature #7] Rappels SMS personnalisables */}
            <div>
              <Label className="mb-2 block">Rappels SMS</Label>
              <div className="space-y-2 p-3 border rounded-lg bg-slate-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newEvent.rappels_config.envoyer_24h}
                    onChange={(e) => setNewEvent(p => ({
                      ...p,
                      rappels_config: { ...p.rappels_config, envoyer_24h: e.target.checked }
                    }))}
                    data-testid="rappel-24h-checkbox"
                  />
                  <span className="text-sm">24 heures avant</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newEvent.rappels_config.envoyer_1h}
                    onChange={(e) => setNewEvent(p => ({
                      ...p,
                      rappels_config: { ...p.rappels_config, envoyer_1h: e.target.checked }
                    }))}
                    data-testid="rappel-1h-checkbox"
                  />
                  <span className="text-sm">1 heure avant</span>
                </label>
                <p className="text-xs text-slate-500">SMS envoyé au client uniquement (fuseau America/Toronto).</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3 pb-4 px-6 -mx-6 border-t mt-3" data-testid="dialog-creer-rdv-footer">
              <Button variant="outline" onClick={() => setShowCreateEvent(false)}>Annuler</Button>
              <Button onClick={handleCreateEvent} data-testid="btn-create-event">Créer l'événement</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 Modal Sélection Représentant (quand on arrive depuis fiche client) */}
      <Dialog open={showSelectRepresentant} onOpenChange={setShowSelectRepresentant}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sélectionner un représentant</DialogTitle>
          </DialogHeader>
          {dossier && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-semibold text-blue-900">{dossier.client_nom}</p>
                <p className="text-sm text-blue-700">{dossier.nom_dossier}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Choisissez le représentant pour ce rendez-vous *</Label>
                <Select 
                  value={selectedRepresentantTemp} 
                  onValueChange={setSelectedRepresentantTemp}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionner un représentant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(employees || [])
                      .filter(isRepresentantCandidate)
                      .map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEmployeeColor(emp.id) }} />
                            {emp.prenom} {emp.nom} ({emp.role || emp.type_employe || 'représentant'})
                          </div>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-gray-600">
                Une fois le représentant sélectionné, vous pourrez choisir une date et un créneau dans son calendrier.
              </p>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowSelectRepresentant(false);
                    window.history.back(); // Retour à la fiche client
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={() => {
                    if (!selectedRepresentantTemp) {
                      toast.error('Veuillez sélectionner un représentant');
                      return;
                    }
                    const emp = employees.find(e => e.id === selectedRepresentantTemp);
                    if (emp) {
                      // Mettre à jour le dossier avec le représentant choisi
                      setDossier(prev => ({ 
                        ...prev, 
                        representant_id: selectedRepresentantTemp, 
                        representant_nom: `${emp.prenom} ${emp.nom}`,
                        employe_id: selectedRepresentantTemp,
                        employe_nom: `${emp.prenom} ${emp.nom}`,
                        employe_telephone: emp.telephone || ''
                      }));
                      // Basculer sur le calendrier de ce représentant
                      setFiltreEmploye(selectedRepresentantTemp);
                      setShowSelectRepresentant(false);
                      toast.success(`Calendrier de ${emp.prenom} ${emp.nom} affiché. Sélectionnez une date pour continuer.`);
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!selectedRepresentantTemp}
                >
                  Afficher le calendrier
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
